import React, { useMemo, useEffect, useState, useRef } from 'react';
import { Layout } from '../components/Layout';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Save, Repeat, ChevronLeft, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useGamification } from '../contexts/GamificationContext';
import { supabase } from '../lib/supabase';

const PROTEIN_OPTIONS = ['Frango', 'Omelete', 'Ovo frito', 'Ovo cozido'] as const;
type ProteinLabel = typeof PROTEIN_OPTIONS[number];

type CardapioItem = {
  data: string;
  proteina: string | string[];
};

type Troca = {
  data: string;
  proteina_original: string;
  proteina_nova?: string;
};

// 🔹 Verifica se a data ainda está dentro do prazo
const isWithinExchangeDeadline = (exchangeDateISO: string): boolean => {
  const now = new Date();
  const targetDate = new Date(exchangeDateISO + 'T00:00:00');
  const targetDay = targetDate.getDay(); // 0=domingo, 1=segunda, ..., 6=sábado

  // Bloqueia finais de semana
  if (targetDay === 0 || targetDay === 6) return false;

  // Regra especial: trocas de segunda podem ser feitas até segunda às 19h
  if (targetDay === 1) {
    const deadline = new Date(targetDate);
    deadline.setHours(19, 0, 0, 0);
    return now <= deadline;
  }

  // Regra geral: até 16h do dia anterior
  const deadline = new Date(targetDate);
  deadline.setDate(deadline.getDate() - 1);
  deadline.setHours(16, 0, 0, 0);

  return now <= deadline;
};

// 🔹 Mensagem de prazo para a UI
const getDeadlineMessage = (): string => {
  const now = new Date();
  const currentTime = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const today = now.toLocaleDateString('pt-BR');

  return `Hoje ${today} as ${currentTime} — Trocas devem ser feitas ate as 16h do dia anterior.`;
};

export const TrocaProteinas: React.FC = () => {
  const { user, isAdmin } = useAuth();  const canBypassDeadline = isAdmin(); // admins podem editar fora do prazo
  const { addActivity } = useGamification();
  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);

  const [cardapioPadrao, setCardapioPadrao] = useState<CardapioItem[]>([]);
  const [mesReferencia, setMesReferencia] = useState<Date>(new Date());
  const [trocas, setTrocas] = useState<Record<string, Troca>>({});
  const [trocasExistentes, setTrocasExistentes] = useState<Record<string, Troca>>({});
  const [applyAllProtein, setApplyAllProtein] = useState<string>("");
  const initialLoadDone = useRef(false);
  const [recarga, setRecarga] = useState(0);

  // 🔹 Recarrega o cardápio quando ele for alterado (tempo real) ou ao voltar para a aba
  useEffect(() => {
    const recarregar = () => {
      if (document.visibilityState === 'visible') setRecarga(n => n + 1);
    };
    window.addEventListener('focus', recarregar);
    document.addEventListener('visibilitychange', recarregar);
    const canal = supabase
      .channel('trocas-cardapio-itens')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'cardapio_itens' }, () => setRecarga(n => n + 1))
      .subscribe();
    return () => {
      window.removeEventListener('focus', recarregar);
      document.removeEventListener('visibilitychange', recarregar);
      supabase.removeChannel(canal);
    };
  }, []);

  const fetchCardapioForMonth = async (month: Date) => {
    const inicioMes = format(startOfMonth(month), 'yyyy-MM-dd');
    const fimMes = format(endOfMonth(month), 'yyyy-MM-dd');

    const { data: cardapioData, error: cardapioError } = await supabase
      .from('cardapio_itens')
      .select('*')
      .gte('data', inicioMes)
      .lte('data', fimMes)
      .eq('publicado', true)
      .order('data', { ascending: true });

    if (cardapioError) throw cardapioError;
    return cardapioData || [];
  };

  useEffect(() => {
    (async () => {
      try {
        if (recarga === 0) setLoadingExisting(true);
        console.log('[TROCAS] Carregando cardápio do Supabase...');

        let cardapioData = await fetchCardapioForMonth(mesReferencia);

        if (cardapioData.length === 0 && !initialLoadDone.current) {
          const nextMonth = addMonths(mesReferencia, 1);
          const nextData = await fetchCardapioForMonth(nextMonth);
          if (nextData.length > 0) {
            cardapioData = nextData;
            initialLoadDone.current = true;
            setMesReferencia(nextMonth);
            return;
          }
        }
        initialLoadDone.current = true;

        if (cardapioData.length === 0) {
          console.log('[TROCAS] Nenhum cardápio encontrado.');
          setCardapioPadrao([]);
          setLoadingExisting(false);
          return;
        }

        const padrao: CardapioItem[] = cardapioData.map(item => ({
          data: format(new Date(item.data + 'T00:00:00'), 'dd/MM/yyyy'),
          proteina: item.proteinas || [],
        }));

        setCardapioPadrao(padrao);
        console.log('[TROCAS] Cardápio carregado, itens:', padrao.length);

        if (!user?.dbId) {
          setLoadingExisting(false);
          return;
        }

        const mesAtual = cardapioData[0].data.substring(0, 7);
        const fromDate = `${mesAtual}-01`;
        const toDate = format(endOfMonth(new Date(fromDate + 'T00:00:00')), 'yyyy-MM-dd');

        const { data: trocasData, error } = await supabase
          .from('trocas_proteina')
          .select('*')
          .eq('usuario_id', user.dbId)
          .gte('data_troca', fromDate)
          .lte('data_troca', toDate);

        if (error) throw error;

        const map: Record<string, Troca> = {};
        for (const t of trocasData || []) {
          map[t.data_troca] = {
            data: t.data_troca,
            proteina_original: t.proteina_original || '',
            proteina_nova: t.proteina_nova,
          };
        }
        setTrocasExistentes(map);
      } catch (e) {
        console.error('[TROCAS] Erro ao carregar cardápio:', e);
        toast.error('Falha ao carregar cardápio.');
      } finally {
        setLoadingExisting(false);
      }
    })();
  }, [user?.dbId, mesReferencia, recarga]);

  const handlePreviousMonth = () => {
    setMesReferencia(prev => subMonths(prev, 1));
  };

  const handleNextMonth = () => {
    setMesReferencia(prev => addMonths(prev, 1));
  };

  // 🔹 Mapeia proteína por data
  const originalByDate = useMemo(() => {
    const map: Record<string, string> = {};
    for (const it of cardapioPadrao) {
      if (!it.data) continue;
      let [dd, mm, yyyy] = it.data.split('/');
      if (dd.length === 1) dd = '0' + dd;
      if (mm.length === 1) mm = '0' + mm;
      const iso = `${yyyy}-${mm}-${dd}`;
      let prot = '';
      if (Array.isArray(it.proteina)) prot = it.proteina.join(' + ');
      else prot = it.proteina || '';
      map[iso] = prot.trim();
    }
    return map;
  }, [cardapioPadrao]);

  const diasDoMes = useMemo(() => {
    const todos = eachDayOfInterval({
      start: startOfMonth(mesReferencia),
      end: endOfMonth(mesReferencia),
    });
    return todos.filter(d => {
      const day = d.getDay();
      return day !== 0 && day !== 6;
    });
  }, [mesReferencia]);

  const getCurrentValue = (dataISO: string): string => {
    if (trocas[dataISO]?.proteina_nova) return trocas[dataISO].proteina_nova;
    if (trocasExistentes[dataISO]?.proteina_nova) return trocasExistentes[dataISO].proteina_nova;
    return '';
  };

  const handleChange = (dataISO: string, nova: string) => {
    const original = originalByDate[dataISO] || '';
    if (!nova) {
      setTrocas(prev => {
        const copy = { ...prev };
        delete copy[dataISO];
        return copy;
      });
      return;
    }
    if (!PROTEIN_OPTIONS.includes(nova as ProteinLabel)) return;
    setTrocas(prev => ({ ...prev, [dataISO]: { data: dataISO, proteina_original: original, proteina_nova: nova } }));
  };

  const aplicarParaTodos = () => {
    if (!applyAllProtein) {
      toast('Escolha uma proteína.');
      return;
    }
    const target = applyAllProtein;
    setTrocas(prev => {
      const copy = { ...prev };
      for (const d of diasDoMes) {
        const iso = format(d, 'yyyy-MM-dd');
        const original = originalByDate[iso];
        if (original && (canBypassDeadline || isWithinExchangeDeadline(iso))) {
          copy[iso] = { data: iso, proteina_original: original, proteina_nova: target };
        }
      }
      return copy;
    });
    toast.success('Proteína aplicada a todos os dias dentro do prazo.');
  };

  const salvar = async () => {
    const payload = Object.values(trocas).filter(t => t.proteina_nova && t.proteina_original);
    if (payload.length === 0) {
      toast('Nenhuma troca válida.');
      return;
    }

    setLoading(true);
    try {
      if (!user?.dbId) throw new Error('Usuário não autenticado');

      const insertData = payload.map(t => ({
        usuario_id: user.dbId,
        data_troca: t.data,
        proteina_original: t.proteina_original,
        proteina_nova: t.proteina_nova,
      }));

      const { error } = await supabase
        .from('trocas_proteina')
        .upsert(insertData, { onConflict: 'usuario_id,data_troca' });

      if (error) throw error;

      setTrocasExistentes(prev => {
        const updated = { ...prev };
        for (const t of payload) {
          updated[t.data] = {
            data: t.data,
            proteina_original: t.proteina_original,
            proteina_nova: t.proteina_nova,
          };
        }
        return updated;
      });

      payload.forEach(t => {
        addActivity('protein_exchange', `Trocou proteína do dia ${format(new Date(t.data), 'dd/MM')}`, {
          data: t.data,
          proteinaNova: t.proteina_nova,
        });
      });

      setTrocas({});
      toast.success(`${payload.length} troca(s) salva(s)!`);

    } catch (e: any) {
      console.error('[TROCAS] Erro ao salvar trocas:', e);
      toast.error(e.message || 'Erro ao salvar.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingExisting)
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin h-12 w-12 rounded-full border-b-2 border-primary"></div>
        </div>
      </Layout>
    );

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-4">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700/50 rounded-lg p-4">
          <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-1">
            As trocas de proteinas devem ser solicitadas <strong>ate as 16h do dia anterior</strong>.
          </p>
          <p className="text-xs text-yellow-600 dark:text-yellow-400">{getDeadlineMessage()}</p>
        </div>

        <div className="flex items-end justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-on-surface">Troca de Proteinas</h1>
            <div className="flex items-center gap-1 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg px-3 py-1.5">
              <button
                onClick={handlePreviousMonth}
                className="p-1 hover:bg-surface-container-low rounded transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-secondary" />
              </button>
              <span className="text-sm font-medium text-on-surface px-2 capitalize min-w-[120px] text-center">
                {format(mesReferencia, 'MMMM yyyy', { locale: ptBR })}
              </span>
              <button
                onClick={handleNextMonth}
                className="p-1 hover:bg-surface-container-low rounded transition-colors"
              >
                <ChevronRight className="w-4 h-4 text-secondary" />
              </button>
            </div>
          </div>
          <div className="flex gap-2">
            <select
              className="border border-outline-variant rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-on-surface"
              value={applyAllProtein}
              onChange={(e) => setApplyAllProtein(e.target.value)}
            >
              <option value="">Trocar todos os dias para...</option>
              {PROTEIN_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <button
              onClick={aplicarParaTodos}
              disabled={!applyAllProtein}
              className="inline-flex items-center gap-2 px-4 py-2 border border-outline-variant rounded-lg hover:bg-surface-container-low text-on-surface"
            >
              <Repeat className="w-4 h-4" /> Aplicar em todos
            </button>
            <button
              onClick={salvar}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
            >
              <Save className="w-4 h-4" /> {loading ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>

        {cardapioPadrao.length === 0 ? (
          <div className="bg-primary/5 border border-outline-variant rounded-xl p-8 text-center">
            <p className="text-on-surface font-medium">
              Nenhum cardapio publicado para {format(mesReferencia, 'MMMM yyyy', { locale: ptBR })}.
            </p>
            <p className="text-sm text-secondary mt-2">
              Use as setas para navegar entre os meses ou aguarde a publicacao do cardapio.
            </p>
          </div>
        ) : (
        <div className="overflow-auto rounded-xl border dark:border-gray-700 bg-white dark:bg-gray-800">
          <table className="min-w-[860px] w-full text-sm text-on-surface">
            <thead className="bg-surface-container">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-on-surface">Data</th>
                <th className="px-3 py-2 text-left font-semibold text-on-surface">Proteina do Cardapio</th>
                <th className="px-3 py-2 text-left font-semibold text-on-surface">Trocar para</th>
                <th className="px-3 py-2 text-left font-semibold text-on-surface">Status</th>
                <th className="px-3 py-2 text-left font-semibold text-on-surface">Prazo</th>
              </tr>
            </thead>
            <tbody>
              {diasDoMes.map(d => {
                const iso = format(d, 'yyyy-MM-dd');
                const original = originalByDate[iso] || '';
                const prazoOk = isWithinExchangeDeadline(iso);  const withinDeadline = canBypassDeadline || prazoOk;
                const selected = getCurrentValue(iso);
                const isExisting = !!trocasExistentes[iso]?.proteina_nova;

                return (
                  <tr key={iso} className={`border-t border-outline-variant ${!withinDeadline ? 'bg-surface-container-low opacity-60' : ''}`}>
                    <td className="px-3 py-2 text-on-surface">{format(d, 'dd/MM/yyyy (EEE)', { locale: ptBR })}</td>
                    <td className="px-3 py-2 text-on-surface">{original}</td>
                    <td className="px-3 py-2">
                      <select
                        className="border border-outline-variant rounded-lg px-2 py-1 w-full bg-white dark:bg-gray-700 text-on-surface"
                        value={selected}
                        onChange={(e) => handleChange(iso, e.target.value)}
                        disabled={!withinDeadline}
                      >
                        <option value="">-- Manter original --</option>
                        {PROTEIN_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      {isExisting && (
                        <span className="px-2 py-1 text-xs bg-primary/10 text-primary rounded-full">
                          Salvo
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {withinDeadline ? (
                        <span className="px-2 py-1 text-xs bg-primary/10 text-primary rounded-full">
                          {prazoOk ? 'Disponivel' : 'Liberado (admin)'}
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs bg-error/10 text-error rounded-full">
                          Prazo expirado
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </Layout>
  );
};

export default TrocaProteinas;
